import { useEffect, useMemo, useRef, useState } from "react";
import {
  COMMENT_MAX_LENGTH,
  COMMENT_REPORT_REASONS,
  buildCommentShareUrl,
  normalizeCommentBody
} from "../../shared/marketComments.js";
import { formatUtcDateTime } from "../lib/timeDisplay";
import { formatWalletAddress } from "../lib/walletAddress";
import { MarketCommentsSkeleton } from "./skeletons.jsx";
import { serviceName } from "../../shared/brand.js";

function safeText(value) {
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  return "";
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeXHandle(value) {
  const handle = safeText(value).replace(/^@/, "");
  if (!/^[A-Za-z0-9_]{1,15}$/.test(handle)) return "";
  return handle;
}

function commentsDataFor(commentsState) {
  if (isRecord(commentsState?.data)) return commentsState.data;
  if (isRecord(commentsState)) return commentsState;
  return null;
}

function statusFor(status, commentsState) {
  return safeText(status) || safeText(commentsState?.status) || "idle";
}

function formatCommentTime(value) {
  if (value === null || value === undefined || value === "") return "Time unavailable";
  const formatted = formatUtcDateTime(value);
  if (formatted === "Invalid date") return "Time unavailable";
  return `${formatted} UTC`;
}

function boundedMaxLength(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 1) return COMMENT_MAX_LENGTH;
  return Math.floor(numeric);
}

function publicOriginFrom(value) {
  const explicit = safeText(value);
  if (explicit) return explicit;
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return "";
}

const COMMENT_TARGET_AUTOPAGE_LIMIT = 3;
const commentTargetIdPattern = /^[A-Za-z0-9_-]{1,80}$/;

// eslint-disable-next-line react-refresh/only-export-components
export function buildCommentTargetId(commentId) {
  return `comment-${safeText(commentId)}`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildCommentSubmitFailureState(draft, error) {
  return {
    draft: safeText(draft),
    error: error?.message || "Could not post comment.",
    isSubmitting: false
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildCommentSubmitSuccessState() {
  return { draft: "", error: "", isSubmitting: false };
}

// eslint-disable-next-line react-refresh/only-export-components
export async function runCommentAction(action, fallbackMessage) {
  try {
    await action?.();
    return { error: "" };
  } catch (error) {
    return { error: error?.message || fallbackMessage };
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function shouldRequestMoreCommentsForTarget({
  targetCommentId,
  targetExists,
  nextCursor,
  currentStatus,
  autoPagesRequested = 0,
  maxAutoPages = COMMENT_TARGET_AUTOPAGE_LIMIT
}) {
  const safeTargetId = safeText(targetCommentId);
  return Boolean(
    commentTargetIdPattern.test(safeTargetId) &&
      !targetExists &&
      nextCursor &&
      currentStatus === "ready" &&
      Number(autoPagesRequested || 0) < Number(maxAutoPages || 0)
  );
}

function CommentIdentity({ comment }) {
  const handle = comment?.displayKind === "linked_x" || comment?.displayKind === "sns_x" ? normalizeXHandle(comment.xHandle) : "";
  if (handle) {
    return (
      <a
        href={`https://x.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-blue-300 underline-offset-4 hover:text-blue-200 hover:underline"
      >
        {`@${handle}`}
      </a>
    );
  }

  return <span className="font-medium text-zinc-200">{formatWalletAddress(comment?.walletAddress) || "Unknown wallet"}</span>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function lockedComposerCopy(viewer) {
  if (!viewer?.isAuthenticated) return "Connect your wallet to join the discussion after you vote.";
  if (!viewer?.hasVotedInMarket) return "Vote in this market to post a comment.";
  if (!viewer?.hasLinkedX) return "Link your X account to post a comment.";
  return "";
}

function LockedComposer({ viewer, onLinkX }) {
  const copy = lockedComposerCopy(viewer);

  return (
    <section
      className="min-h-[9rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3"
      data-cls-measure="market-comments-composer"
    >
      <p className="text-sm font-medium text-zinc-200">Join the discussion</p>
      <p className="mt-2 text-sm text-zinc-400">{copy}</p>
      {viewer?.isAuthenticated && viewer?.hasVotedInMarket && !viewer?.hasLinkedX ? (
        <button
          type="button"
          className="theme-button-secondary mt-3"
          onClick={() => onLinkX?.()}
        >
          Link X account
        </button>
      ) : null}
    </section>
  );
}

function CommentComposer({ maxLength, onSubmit }) {
  const [submitState, setSubmitState] = useState(buildCommentSubmitSuccessState);
  const normalizedDraft = useMemo(() => normalizeCommentBody(submitState.draft), [submitState.draft]);
  const remaining = Math.max(0, maxLength - normalizedDraft.length);

  async function handleSubmit(event) {
    event.preventDefault();
    const body = normalizeCommentBody(submitState.draft);
    if (!body || submitState.isSubmitting) return;
    setSubmitState((previous) => ({ ...previous, error: "", isSubmitting: true }));
    try {
      await onSubmit?.(body);
      setSubmitState(buildCommentSubmitSuccessState());
    } catch (error) {
      setSubmitState(buildCommentSubmitFailureState(submitState.draft, error));
    }
  }

  return (
    <form
      className="min-h-[9rem] space-y-3 rounded-xl border border-zinc-800 bg-zinc-950/50 p-3"
      data-cls-measure="market-comments-composer"
      onSubmit={handleSubmit}
    >
      <label className="block text-sm font-medium text-zinc-200" htmlFor="market-comment-draft">
        Add a voter comment
      </label>
      <textarea
        id="market-comment-draft"
        className="min-h-[5rem] w-full resize-y rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25"
        maxLength={maxLength}
        value={submitState.draft}
        disabled={submitState.isSubmitting}
        onChange={(event) => setSubmitState({ draft: event.target.value, error: "", isSubmitting: false })}
      />
      {submitState.error ? <p className="text-sm text-rose-300">{submitState.error}</p> : null}
      <div className="flex flex-col gap-2 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
        <span>{remaining} characters remaining</span>
        <button
          type="submit"
          className="min-h-9 shrink-0 rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500"
          disabled={!normalizedDraft || submitState.isSubmitting}
        >
          {submitState.isSubmitting ? "Posting..." : "Post comment"}
        </button>
      </div>
    </form>
  );
}

function SortControl({ sort, onSortChange }) {
  return (
    <div className="inline-flex rounded-lg border border-zinc-800 bg-zinc-950/70 p-1" aria-label="Sort comments">
      {["top", "newest"].map((option) => (
        <button
          key={option}
          type="button"
          className={[
            "min-h-8 rounded-md px-3 text-xs font-semibold capitalize",
            sort === option ? "bg-zinc-200 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"
          ].join(" ")}
          aria-pressed={sort === option}
          onClick={() => onSortChange?.(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function CommentCard({ comment, market, publicSiteOrigin, affiliateId, onLike, onReport, onHide }) {
  const [actionState, setActionState] = useState({
    error: "",
    isLiking: false,
    isHiding: false
  });
  const commentId = safeText(comment?.id);
  const body = safeText(comment?.body);
  const targetId = buildCommentTargetId(commentId);
  const authorPositionLabel = safeText(comment?.authorPositionLabel);
  const snapshotLabel = safeText(comment?.marketSnapshotLabel);
  const marketId = safeText(market?.id) || safeText(comment?.marketId);
  const marketTitle = safeText(market?.title) || serviceName;
  const likeCount = Number.isFinite(Number(comment?.likeCount)) ? Number(comment.likeCount) : 0;
  const shareUrl = buildCommentShareUrl({
    origin: publicSiteOrigin,
    marketId,
    marketTitle,
    commentId,
    commentBody: body,
    affiliateId
  });

  async function handleLike() {
    if (!onLike || actionState.isLiking) return;
    setActionState((previous) => ({ ...previous, error: "", isLiking: true }));
    const result = await runCommentAction(() => onLike(commentId), "Could not update like.");
    setActionState((previous) => ({ ...previous, error: result.error, isLiking: false }));
  }

  async function handleHide() {
    if (!onHide || actionState.isHiding) return;
    setActionState((previous) => ({ ...previous, error: "", isHiding: true }));
    const result = await runCommentAction(() => onHide(commentId), "Could not hide comment.");
    setActionState((previous) => ({ ...previous, error: result.error, isHiding: false }));
  }

  return (
    <article
      id={targetId}
      tabIndex={-1}
      className="min-w-0 scroll-mt-24 rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25"
    >
      <header className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm">
            <CommentIdentity comment={comment} />
            {authorPositionLabel ? <span className="ml-2 text-xs font-medium text-zinc-400">Backed {authorPositionLabel}</span> : null}
          </p>
          <p className="text-xs text-zinc-500">
            {snapshotLabel ? `${snapshotLabel} ` : ""}
            {formatCommentTime(comment?.createdAt)}
          </p>
        </div>
      </header>
      <p className="mt-3 min-w-0 whitespace-pre-wrap break-words text-sm leading-6 text-zinc-200">{body}</p>
      {actionState.error ? <p className="mt-2 text-xs text-rose-300">{actionState.error}</p> : null}
      <footer className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <div className="inline-flex items-center gap-1 text-zinc-400">
          <button
            type="button"
            className={[
              "inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-rose-400/50 disabled:cursor-not-allowed disabled:opacity-50",
              comment?.viewerLiked ? "text-rose-400 hover:text-rose-300" : "hover:text-rose-300"
            ].join(" ")}
            aria-label={actionState.isLiking ? "Saving like" : comment?.viewerLiked ? "Unlike comment" : "Like comment"}
            aria-pressed={Boolean(comment?.viewerLiked)}
            title={comment?.viewerLiked ? "Unlike comment" : "Like comment"}
            disabled={actionState.isLiking}
            onClick={handleLike}
          >
            <HeartIcon filled={Boolean(comment?.viewerLiked)} />
          </button>
          <span>{likeCount}</span>
        </div>
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md border border-zinc-800 px-2 py-1 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
        >
          Share on X
        </a>
        <CommentReportForm commentId={commentId} onReport={onReport} />
        {comment?.viewerCanDelete ? (
          <button
            type="button"
            className="min-w-[4.75rem] rounded-md border border-zinc-800 px-2 py-1 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100 disabled:cursor-not-allowed disabled:text-zinc-500"
            disabled={actionState.isHiding}
            onClick={handleHide}
          >
            {actionState.isHiding ? "Hiding..." : "Hide"}
          </button>
        ) : null}
      </footer>
    </article>
  );
}

function HeartIcon({ filled = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      aria-hidden="true"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" />
    </svg>
  );
}

function CommentReportForm({ commentId, onReport }) {
  const [reportState, setReportState] = useState({
    reason: COMMENT_REPORT_REASONS[0],
    details: "",
    error: "",
    success: "",
    isSubmitting: false
  });

  async function submitReport(event) {
    event.preventDefault();
    if (!onReport || reportState.isSubmitting) return;
    setReportState((previous) => ({ ...previous, error: "", success: "", isSubmitting: true }));
    try {
      await onReport(commentId, { reason: reportState.reason, details: reportState.details });
      setReportState((previous) => ({
        ...previous,
        details: "",
        error: "",
        success: "Report submitted.",
        isSubmitting: false
      }));
    } catch (error) {
      setReportState((previous) => ({
        ...previous,
        error: error?.message || "Could not submit report.",
        success: "",
        isSubmitting: false
      }));
    }
  }

  return (
    <details className="min-w-0 rounded-md border border-zinc-800 px-2 py-1 text-zinc-400 open:w-full open:max-w-md open:p-2">
      <summary className="cursor-pointer list-none hover:text-zinc-100">Report</summary>
      <form className="mt-2 space-y-2" onSubmit={submitReport}>
        <label className="block text-xs font-medium text-zinc-300" htmlFor={`report-reason-${commentId}`}>
          Report reason
        </label>
        <select
          id={`report-reason-${commentId}`}
          className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-200"
          value={reportState.reason}
          disabled={reportState.isSubmitting}
          onChange={(event) => setReportState((previous) => ({ ...previous, reason: event.target.value, error: "", success: "" }))}
        >
          {COMMENT_REPORT_REASONS.map((reason) => (
            <option key={reason} value={reason}>
              {formatReportReason(reason)}
            </option>
          ))}
        </select>
        <textarea
          className="min-h-16 w-full resize-y rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-200"
          maxLength={500}
          value={reportState.details}
          disabled={reportState.isSubmitting}
          onChange={(event) => setReportState((previous) => ({ ...previous, details: event.target.value, error: "", success: "" }))}
          placeholder="Optional details"
        />
        {reportState.error ? <p className="text-xs text-rose-300">{reportState.error}</p> : null}
        {reportState.success ? <p className="text-xs text-emerald-300">{reportState.success}</p> : null}
        <button
          type="submit"
          className="rounded-md border border-zinc-700 px-2 py-1 text-xs font-semibold text-zinc-200 hover:border-zinc-500 disabled:cursor-not-allowed disabled:text-zinc-500"
          disabled={reportState.isSubmitting}
        >
          {reportState.isSubmitting ? "Submitting..." : "Submit report"}
        </button>
      </form>
    </details>
  );
}

function formatReportReason(reason) {
  const text = safeText(reason).replace(/_/g, " ");
  return text ? `${text.slice(0, 1).toUpperCase()}${text.slice(1)}` : "";
}

function MarketCommentsContent({
  currentStatus,
  data,
  error,
  market,
  sort,
  targetCommentId,
  publicSiteOrigin,
  affiliateId,
  onSortChange,
  onSubmit,
  onLike,
  onReport,
  onHide,
  onLoadMore,
  onLinkX
}) {
  const targetAutoPageRef = useRef({ targetCommentId: "", requestedPages: 0 });
  const comments = Array.isArray(data?.comments) ? data.comments.filter(isRecord) : [];
  const viewer = isRecord(data?.viewer) ? data.viewer : {};
  const maxLength = boundedMaxLength(data?.limits?.maxLength);
  const activeSort = safeText(sort) || safeText(data?.sort) || "top";
  const publicOrigin = publicOriginFrom(publicSiteOrigin);
  const safeMarket = {
    id: safeText(market?.id) || safeText(data?.marketId),
    title: safeText(market?.title)
  };
  const hasLoadErrorWithoutData = currentStatus === "error" && !data;
  const hasStaleError = currentStatus === "error" && Boolean(data);
  const ready = currentStatus === "ready" || currentStatus === "refreshing" || hasStaleError;
  const canLoadMore = Boolean(data?.nextCursor) && !hasLoadErrorWithoutData;
  const isLoadingMore = currentStatus === "refreshing" && canLoadMore;
  const errorMessage = safeText(error) || "Please try again shortly.";

  useEffect(() => {
    const targetId = targetCommentId ? buildCommentTargetId(targetCommentId) : "";
    if (!targetId || typeof document === "undefined") return;
    const safeTargetCommentId = safeText(targetCommentId);
    if (targetAutoPageRef.current.targetCommentId !== safeTargetCommentId) {
      targetAutoPageRef.current = { targetCommentId: safeTargetCommentId, requestedPages: 0 };
    }
    const target = document.getElementById(targetId);
    if (!target) {
      if (
        shouldRequestMoreCommentsForTarget({
          targetCommentId,
          targetExists: false,
          nextCursor: data?.nextCursor,
          currentStatus,
          autoPagesRequested: targetAutoPageRef.current.requestedPages
        })
      ) {
        targetAutoPageRef.current.requestedPages += 1;
        onLoadMore?.();
      }
      return;
    }
    target.scrollIntoView({ block: "center" });
    target.focus({ preventScroll: true });
  }, [targetCommentId, comments.length, currentStatus, data?.nextCursor, onLoadMore]);

  return (
    <section
      className="min-h-[32rem] min-w-0 space-y-4 rounded-2xl border border-zinc-800/80 bg-zinc-950/35 p-3"
      data-cls-measure="market-comments"
    >
      <header className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-200">Market Comments</h3>
          <p className="text-xs text-zinc-400">
            Comments are public to read. Posting requires a signed-in wallet, linked X account, and confirmed vote in this market.
          </p>
        </div>
        <SortControl sort={activeSort} onSortChange={onSortChange} />
      </header>

      {hasLoadErrorWithoutData ? null : viewer.isAuthenticated && viewer.hasVotedInMarket && viewer.hasLinkedX ? (
        <CommentComposer maxLength={maxLength} onSubmit={onSubmit} />
      ) : (
        <LockedComposer viewer={viewer} onLinkX={onLinkX} />
      )}

      {hasStaleError ? (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
          Showing cached comments. {errorMessage}
        </p>
      ) : null}

      <div className="min-h-[17rem] space-y-3" data-cls-measure="market-comments-list">
        {hasLoadErrorWithoutData ? (
          <div className="min-h-[8rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 text-sm text-zinc-400">
            <p className="font-medium text-zinc-200">Market comments are unavailable right now.</p>
            <p className="mt-2">{errorMessage}</p>
          </div>
        ) : comments.length ? (
          comments.map((comment) => (
            <CommentCard
              key={safeText(comment.id) || safeText(comment.createdAt)}
              comment={comment}
              market={safeMarket}
              publicSiteOrigin={publicOrigin}
              affiliateId={affiliateId}
              onLike={onLike}
              onReport={onReport}
              onHide={onHide}
            />
          ))
        ) : ready ? (
          <div className="min-h-[8rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 text-sm text-zinc-500">
            No comments yet. Vote in this market to start the discussion.
          </div>
        ) : null}
      </div>

      {canLoadMore ? (
        <div className="flex justify-center">
          <button
            type="button"
            className="min-h-10 rounded-md border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-500 hover:text-white disabled:cursor-not-allowed disabled:border-zinc-800 disabled:text-zinc-500"
            disabled={isLoadingMore}
            onClick={() => onLoadMore?.()}
          >
            {isLoadingMore ? "Loading comments..." : "Load more comments"}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function MarketComments({
  status = "",
  commentsState = null,
  error = "",
  market = null,
  sort = "",
  targetCommentId = "",
  publicSiteOrigin = "",
  affiliateId = "",
  onSortChange,
  onSubmit,
  onLike,
  onReport,
  onHide,
  onLoadMore,
  onLinkX
}) {
  const currentStatus = statusFor(status, commentsState);
  const data = commentsDataFor(commentsState);

  if ((currentStatus === "idle" || currentStatus === "loading") && !data) {
    return <MarketCommentsSkeleton />;
  }

  return (
    <MarketCommentsContent
      currentStatus={currentStatus}
      data={data}
      error={error}
      market={market}
      sort={sort}
      targetCommentId={targetCommentId}
      publicSiteOrigin={publicSiteOrigin}
      affiliateId={affiliateId}
      onSortChange={onSortChange}
      onSubmit={onSubmit}
      onLike={onLike}
      onReport={onReport}
      onHide={onHide}
      onLoadMore={onLoadMore}
      onLinkX={onLinkX}
    />
  );
}
